<script setup lang="ts">
/**
 * Phase 17.11: preview of the newer built-in apps (countdown, QR code, text, ticker, menu board, world clock, web
 * page, YouTube, Google Slides), laid out like the player's MoreApps.kt. Sizes are container-query units, so the same
 * component previews an app in Customize App and inside a campaign section of any size. `live` = play (tick,
 * scroll, load web content); off in thumbnails.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import qrcode from 'qrcode-generator';
import { type DashboardCheck, dashboardEmbedUrl, googleSlidesEmbedUrl, type WidgetConfig } from '@codot/contracts';
import { useI18n } from 'vue-i18n';
import { mediaApi } from '../../api/content.api';
import { fontFamilyCss } from '../../utils/fonts';
import CalendarPreview from './CalendarPreview.vue';
import ChartPreview from './ChartPreview.vue';
import PhotosPreview from './PhotosPreview.vue';
import PeoplePreview from './PeoplePreview.vue';
import MeetingRoomPreview from './MeetingRoomPreview.vue';
import StreamPreview from './StreamPreview.vue';
import SocialWallPreview from './SocialWallPreview.vue';

const props = withDefaults(defineProps<{ config: WidgetConfig; zone?: string; live?: boolean }>(), { zone: 'Asia/Dubai', live: true });

const { t } = useI18n();
/** One check per link for every preview on the page. */
const checks = new Map<string, Promise<DashboardCheck>>();
const checkLink = (url: string) => checks.get(url) ?? (checks.set(url, mediaApi.dashboardCheck(url)), checks.get(url)!);
const now = ref(Date.now());
const timer = setInterval(() => props.live && (now.value = Date.now()), 1000);
onBeforeUnmount(() => clearInterval(timer));

/** min(h% of height, w% of width), like ZoneMetrics.fit on the player. */
const fit = (h: number, w: number) => `min(${h * 100}cqh, ${w * 100}cqw)`;
const SIZE: Record<string, number> = { small: 0.045, medium: 0.06, large: 0.08, xlarge: 0.11 };
const arabicDigits = (s: string, on: boolean) => (on ? s.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[Number(d)]!) : s);

/* ---- Countdown ---- */
const countdown = computed(() => {
  if (props.config.type !== 'countdown') return null;
  const c = props.config;
  const zone = c.timezone ?? props.zone;
  // The target is local wall-clock time in `zone`: find the instant whose local time there matches.
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(c.target);
  if (!m) return { invalid: true as const };
  const asUtc = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!);
  const offset = (ms: number) => {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }).formatToParts(ms).map((x) => [x.type, Number(x.value)]));
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(ms / 60000) * 60000;
  };
  const target = asUtc - offset(asUtc);
  const up = c.mode === 'up';
  const raw = up ? now.value - target : target - now.value;
  const done = !up && raw <= 0;
  const s = Math.max(0, Math.floor(raw / 1000));
  const ar = c.language === 'ar';
  const labels = ar ? ['أيام', 'ساعات', 'دقائق', 'ثوانٍ'] : ['Days', 'Hours', 'Minutes', 'Seconds'];
  const units = [
    [String(Math.floor(s / 86400)), labels[0]],
    [String(Math.floor((s % 86400) / 3600)).padStart(2, '0'), labels[1]],
    [String(Math.floor((s % 3600) / 60)).padStart(2, '0'), labels[2]],
    ...(c.showSeconds ? [[String(s % 60).padStart(2, '0'), labels[3]]] : []),
  ].map(([v, l]) => ({ v: arabicDigits(v!, ar), l: l! }));
  return { invalid: false as const, done, units, ar, c };
});

/* ---- QR code ---- */
const qr = computed(() => {
  if (props.config.type !== 'qr_code' || !props.config.data.trim()) return null;
  try {
    const q = qrcode(0, 'M');
    // The library's default byte mode keeps the low 8 bits of each char: feed it UTF-8 bytes (as the player does).
    const utf8 = Array.from(new TextEncoder().encode(props.config.data), (b) => String.fromCharCode(b)).join('');
    q.addData(utf8, 'Byte');
    q.make();
    const n = q.getModuleCount();
    let d = '';
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (q.isDark(y, x)) d += `M${x} ${y}h1v1h-1z`;
    return { n, d };
  } catch {
    return null;
  }
});

/* ---- Menu board: sections into columns, shortest column first (as on the player) ---- */
const menu = computed(() => {
  if (props.config.type !== 'menu_board') return null;
  const c = props.config;
  const cols = Array.from({ length: Math.min(4, Math.max(1, c.columns)) }, () => [] as typeof c.sections);
  const lines = cols.map(() => 0);
  for (const s of c.sections) {
    const i = lines.indexOf(Math.min(...lines));
    cols[i]!.push(s);
    lines[i]! += s.items.length + s.items.filter((it) => it.description).length + (s.name ? 2 : 0);
  }
  const maxLines = Math.max(1, ...lines);
  const title = !!c.title;
  // One item line, as a share of the zone height (bounded by column width).
  const line = Math.min((1 - 0.08 - (title ? 0.14 : 0)) / (maxLines * 1.45 + 1), 1 / cols.length / 14);
  return { c, cols, line };
});

/* ---- World clock ---- */
const world = computed(() => {
  if (props.config.type !== 'world_clock') return null;
  const c = props.config;
  const loc = c.language === 'ar' ? 'ar-AE' : 'en-GB';
  return c.cities.map((city) => {
    let time = '';
    let date = '';
    try {
      time = new Intl.DateTimeFormat(loc, { timeZone: city.timezone, hour: '2-digit', minute: '2-digit', hour12: c.format === '12h', numberingSystem: c.language === 'ar' ? 'arab' : 'latn' }).format(now.value);
      date = new Intl.DateTimeFormat(loc, { timeZone: city.timezone, weekday: 'short', day: 'numeric', month: 'short' }).format(now.value);
    } catch {
      time = '--:--';
    }
    return { label: city.label, time, date };
  });
});

/* ---- BI dashboard: checked first, so a site that can't be shown here says why instead of a white box ---- */
const dashCheck = ref<DashboardCheck | null>(null);
let dashTimer = 0;
watch(
  () => (props.config.type === 'dashboard' && props.live ? (props.config.url ? dashboardEmbedUrl(props.config) : '') : null),
  (url) => {
    clearTimeout(dashTimer);
    dashCheck.value = null;
    if (!url) return;
    dashTimer = window.setTimeout(() => void checkLink(url).then((c) => (dashCheck.value = c), () => (dashCheck.value = null)), 400);
  },
  { immediate: true },
);
onBeforeUnmount(() => clearTimeout(dashTimer));
const dashNote = computed(() => {
  const c = dashCheck.value;
  if (!c || c.verdict === 'ok') return null;
  return t(`appsCat.biCheck_${c.verdict}`, { detail: c.detail ?? '' });
});

/* ---- Web apps ---- */
/** What a web, Slides, playlist or BI app shows when it isn't playing live (a library card, a picker). */
const faviconBroken = ref(false);
const hostOf = (u: string | null | undefined) => {
  try {
    return u ? new URL(u).hostname.replace(/^www\./, '') : '';
  } catch {
    return '';
  }
};
const thumbCard = computed(() => {
  const c = props.config;
  if (c.type === 'youtube') return { bg: 'linear-gradient(135deg,#2b0a0e,#120507)', fg: '#fff', icon: '▶', favicon: '', title: 'YouTube', sub: c.playlistId ? 'Playlist' : '' };
  if (c.type === 'google_slides') return { bg: 'linear-gradient(135deg,#f6b400,#e08a00)', fg: '#1d1300', icon: '▤', favicon: '', title: 'Google Slides', sub: `${c.slideSeconds} s / slide` };
  if (c.type === 'dashboard') return { bg: 'linear-gradient(135deg,#0f2a3d,#0a1622)', fg: '#e6f3ff', icon: '📊', favicon: '', title: hostOf(c.url) || 'Dashboard', sub: 'Dashboard' };
  const host = c.type === 'web_page' ? hostOf(c.url) : '';
  return { bg: 'linear-gradient(135deg,#1b2238,#10141f)', fg: '#eef2ff', icon: '🌐', favicon: host ? `https://${host}/favicon.ico` : '', title: host || 'Web page', sub: c.type === 'web_page' ? c.url.replace(/^https?:\/\//, '') : '' };
});
const webSrc = computed(() => {
  const c = props.config;
  if (c.type === 'web_page') return c.url;
  if (c.type === 'dashboard') return c.url ? dashboardEmbedUrl(c) : null;
  if (c.type === 'google_slides') return googleSlidesEmbedUrl(c);
  if (c.type === 'youtube') {
    const p = new URLSearchParams({ autoplay: '1', mute: c.muted ? '1' : '0', controls: '0', rel: '0', playsinline: '1' });
    if (c.loop) p.set('loop', '1');
    if (c.captions) p.set('cc_load_policy', '1');
    if (c.playlistId) p.set('list', c.playlistId);
    else if (c.loop && c.videoId) p.set('playlist', c.videoId);
    return c.videoId || c.playlistId ? `https://www.youtube-nocookie.com/embed/${c.videoId ?? 'videoseries'}?${p}` : null;
  }
  return null;
});

const look = computed(() =>
  'background' in props.config
    ? { background: props.config.background, color: props.config.foreground, fontFamily: fontFamilyCss(props.config.fontId) }
    : { background: '#000', color: '#fff' },
);
const dirOf = (d: string | undefined) => (d === 'rtl' ? 'rtl' : 'ltr');
const tickerSeconds = computed(() => {
  if (props.config.type !== 'ticker') return 20;
  const chars = props.config.messages.join('     ').length;
  const perChar = { slow: 0.32, medium: 0.2, fast: 0.12 }[props.config.speed];
  return Math.max(6, chars * perChar);
});
</script>

<template>
  <div class="relative h-full w-full overflow-hidden" style="container-type: size" :style="look">
    <ChartPreview v-if="config.type === 'chart'" :config="config" class="absolute inset-0" />
    <PhotosPreview v-else-if="config.type === 'photos'" :config="config" :live="live" class="absolute inset-0" />
    <PeoplePreview v-else-if="config.type === 'people'" :config="config" :zone="zone" :live="live" class="absolute inset-0" />
    <CalendarPreview v-else-if="config.type === 'calendar'" :config="config" :zone="zone" :live="live" class="absolute inset-0" />
    <MeetingRoomPreview v-else-if="config.type === 'meeting_room'" :config="config" :zone="zone" :live="live" class="absolute inset-0" />
    <!-- Social wall app -->
    <SocialWallPreview v-else-if="config.type === 'social_wall'" :config="config" :live="live" class="absolute inset-0" />
    <!-- Countdown -->
    <div v-else-if="countdown" class="flex h-full flex-col items-center justify-center text-center" :dir="!countdown.invalid && countdown.ar ? 'rtl' : 'ltr'">
      <template v-if="!countdown.invalid">
        <p v-if="countdown.c.title" class="font-medium leading-tight" :style="{ fontSize: fit(0.11, 0.06), marginBottom: '4cqh' }">{{ countdown.c.title }}</p>
        <p v-if="countdown.done && countdown.c.doneText" class="font-bold" :style="{ fontSize: fit(0.16, 0.08) }">{{ countdown.c.doneText }}</p>
        <div v-else class="flex" style="gap: 2.5cqw">
          <div v-for="u in countdown.units" :key="u.l" class="flex flex-col items-center">
            <span class="font-bold leading-none tabular-nums" :style="{ fontSize: fit(0.32, 0.85 / (countdown.units.length * 2.6)) }">{{ u.v }}</span>
            <span class="opacity-75" :style="{ fontSize: fit(0.065, 0.035) }">{{ u.l }}</span>
          </div>
        </div>
      </template>
    </div>

    <!-- QR code -->
    <div v-else-if="config.type === 'qr_code'" class="flex h-full flex-col items-center justify-center" style="gap: 3cqh">
      <p v-if="config.caption && config.captionPosition === 'above'" class="text-center font-medium" :style="{ fontSize: fit(0.08, 0.06) }">{{ config.caption }}</p>
      <div class="rounded bg-white" :style="{ width: config.caption ? 'min(70cqh, 86cqw)' : 'min(86cqh, 86cqw)', aspectRatio: '1', padding: '3%' }">
        <svg v-if="qr" :viewBox="`0 0 ${qr.n} ${qr.n}`" class="h-full w-full" shape-rendering="crispEdges" aria-hidden="true"><path :d="qr.d" fill="#000" /></svg>
      </div>
      <p v-if="config.caption && config.captionPosition !== 'above'" class="text-center font-medium" :style="{ fontSize: fit(0.08, 0.06) }">{{ config.caption }}</p>
    </div>

    <!-- Text -->
    <div
      v-else-if="config.type === 'text'"
      class="flex h-full flex-col justify-center"
      style="padding: min(6cqh, 6cqw)"
      :dir="dirOf(config.direction)"
      :class="{ start: 'items-start text-start', center: 'items-center text-center', end: 'items-end text-end' }[config.align]"
    >
      <p v-if="config.headline" class="w-full font-bold leading-tight" :style="{ fontSize: fit((SIZE[config.size] ?? 0.08) * 1.6, 0.09) }">{{ config.headline }}</p>
      <p v-if="config.body" class="w-full whitespace-pre-line opacity-75" :style="{ fontSize: fit(SIZE[config.size] ?? 0.08, 0.06), marginTop: config.headline ? '3cqh' : '0' }">{{ config.body }}</p>
    </div>

    <!-- Ticker -->
    <div v-else-if="config.type === 'ticker'" class="flex h-full items-center overflow-hidden whitespace-nowrap" :dir="dirOf(config.direction)">
      <p
        :key="`${config.messages.join('|')}-${tickerSeconds}-${config.direction}`"
        class="inline-block"
        :class="live ? ['ticker', 'ps-[100%]', config.direction === 'rtl' ? 'ticker-rtl' : ''] : 'px-[3cqw]'"
        :style="{ animationDuration: `${tickerSeconds}s`, fontSize: `${{ small: 38, medium: 46, large: 55, xlarge: 66 }[config.size]}cqh` }"
      >
        {{ config.messages.join(`     ${config.separator || '•'}     `) }}
      </p>
    </div>

    <!-- Menu board -->
    <div v-else-if="menu" class="flex h-full flex-col" style="padding: min(4cqh, 4cqw)" :dir="dirOf(menu.c.direction)">
      <p v-if="menu.c.title" class="truncate font-bold" :style="{ fontSize: fit(0.09, 0.06), marginBottom: '3cqh' }">{{ menu.c.title }}</p>
      <div class="flex min-h-0 flex-1" style="gap: min(4cqh, 4cqw)">
        <div v-for="(col, i) in menu.cols" :key="i" class="min-w-0 flex-1">
          <div v-for="(s, k) in col" :key="k" :style="{ marginBottom: `${menu.line * 50}cqh` }">
            <p v-if="s.name" class="truncate font-bold" :style="{ color: menu.c.accent, fontSize: `${menu.line * 125}cqh`, marginBottom: `${menu.line * 40}cqh` }">{{ s.name }}</p>
            <div v-for="(it, j) in s.items" :key="j" :style="{ marginBottom: `${menu.line * 35}cqh` }">
              <div class="flex items-start gap-2" :style="{ fontSize: `${menu.line * 100}cqh` }">
                <span class="min-w-0 flex-1 truncate font-medium">{{ it.name }}</span>
                <span v-if="it.price" class="shrink-0 font-bold tabular-nums" :style="{ color: menu.c.accent }">{{ it.price }}</span>
              </div>
              <p v-if="it.description" class="truncate opacity-75" :style="{ fontSize: `${menu.line * 78}cqh` }">{{ it.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- World clock -->
    <div v-else-if="world" class="flex h-full w-full" style="flex-direction: row" :dir="config.type === 'world_clock' && config.language === 'ar' ? 'rtl' : 'ltr'">
      <div v-for="(c, i) in world" :key="i" class="flex min-w-0 flex-1 flex-col items-center justify-center text-center">
        <span class="truncate opacity-75" :style="{ fontSize: fit(0.16 * 1, 0.1 / world.length) }">{{ c.label }}</span>
        <span class="font-medium leading-tight tabular-nums" :style="{ fontSize: fit(0.36, 0.2 / world.length) }">{{ c.time }}</span>
        <span v-if="config.type === 'world_clock' && config.showDate" class="opacity-75" :style="{ fontSize: fit(0.12, 0.08 / world.length) }">{{ c.date }}</span>
      </div>
    </div>

    <!-- Web page / YouTube / Google Slides -->
    <template v-else-if="config.type === 'web_page' || config.type === 'youtube' || config.type === 'google_slides' || config.type === 'dashboard'">
      <iframe
        v-if="live && webSrc && !dashNote"
        :src="webSrc"
        class="h-full w-full border-0 bg-black"
        sandbox="allow-scripts allow-same-origin allow-presentation"
        allow="autoplay; encrypted-media"
        referrerpolicy="strict-origin-when-cross-origin"
        :style="(config.type === 'web_page' || config.type === 'dashboard') && config.zoom !== 100 ? { width: `${10000 / config.zoom}%`, height: `${10000 / config.zoom}%`, transform: `scale(${config.zoom / 100})`, transformOrigin: '0 0' } : undefined"
        :title="config.type"
      />
      <div v-else-if="dashNote" class="flex h-full flex-col items-center justify-center gap-[4cqh] bg-slate-900 px-[8cqw] text-center text-white" role="status" data-testid="bi-note">
        <span :style="{ fontSize: fit(0.14, 0.07) }" aria-hidden="true">{{ dashCheck?.verdict === 'blocked' ? '🚫' : dashCheck?.verdict === 'login' ? '🔒' : '⚠' }}</span>
        <span class="leading-snug" :style="{ fontSize: fit(0.065, 0.032) }">{{ dashNote }}</span>
      </div>
      <!-- Still thumbnails (cards, pickers): the video's own frame, or a card that says what it is and where from. -->
      <div v-else-if="config.type === 'youtube' && config.videoId" class="relative h-full w-full bg-black">
        <img :src="`https://i.ytimg.com/vi/${config.videoId}/hqdefault.jpg`" alt="" class="absolute inset-0 h-full w-full object-cover" loading="lazy" referrerpolicy="no-referrer" />
        <span class="absolute inset-0 m-auto flex h-[22cqh] w-[32cqh] items-center justify-center rounded-[5cqh] bg-[#ff0033] shadow-lg" aria-hidden="true">
          <svg viewBox="0 0 24 24" class="h-[11cqh] w-[11cqh]" fill="white"><path d="M8 5v14l11-7z" /></svg>
        </span>
      </div>
      <div
        v-else
        class="flex h-full w-full flex-col justify-start gap-[2cqh] p-[6cqh]"
        :style="{ background: thumbCard.bg, color: thumbCard.fg }"
      >
        <span class="flex items-center gap-[3cqh] font-semibold" :style="{ fontSize: fit(0.1, 0.05) }">
          <img v-if="thumbCard.favicon && !faviconBroken" :src="thumbCard.favicon" alt="" class="h-[1.2em] w-[1.2em] rounded" referrerpolicy="no-referrer" @error="faviconBroken = true" />
          <span v-else aria-hidden="true">{{ thumbCard.icon }}</span>
          <span class="truncate">{{ thumbCard.title }}</span>
        </span>
        <span class="truncate opacity-75" :style="{ fontSize: fit(0.075, 0.04) }">{{ thumbCard.sub }}</span>
      </div>
    </template>
    <!-- Streaming video -->
    <StreamPreview v-else-if="config.type === 'stream'" :config="config" :live="live" class="absolute inset-0" />
  </div>
</template>

<style scoped>
.ticker {
  animation: ticker linear infinite;
  line-height: 1;
}
.ticker-rtl {
  animation-name: ticker-rtl;
}
.paused {
  animation-play-state: paused;
}
@keyframes ticker {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
}
@keyframes ticker-rtl {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
}
@media (prefers-reduced-motion: reduce) {
  .ticker {
    animation: none;
    padding-inline-start: 0;
  }
}
</style>
